import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { unitPriceFor } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const KEY = "ds_cart";

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (!user) return;
    const ids = items.map((i) => i.product_id);
    const t = setTimeout(() => {
      api.put("/cart", { product_ids: ids }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [items, user]);

  const addItem = (product, quantity = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found)
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: Math.min(i.quantity + quantity, product.stock || 999) } : i
        );
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          sku: product.sku,
          image: (product.images || [])[0] || "",
          price: Number(product.price),
          wholesale_price: product.wholesale_price,
          wholesale_min_qty: product.wholesale_min_qty,
          is_adult: !!product.is_adult,
          stock: product.stock,
          quantity,
        },
      ];
    });
  };

  const setQty = (productId, quantity) =>
    setItems((prev) =>
      prev.map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(1, quantity) } : i))
    );

  const remove = (productId) => setItems((prev) => prev.filter((i) => i.product_id !== productId));
  const clear = () => setItems([]);

  const value = useMemo(() => {
    const lines = items.map((i) => {
      const unit = unitPriceFor(i, i.quantity);
      return { ...i, unit_price: unit, total: unit * i.quantity, is_wholesale: unit !== Number(i.price) };
    });
    return {
      items: lines,
      addItem,
      setQty,
      remove,
      clear,
      count: lines.reduce((s, i) => s + i.quantity, 0),
      subtotal: lines.reduce((s, i) => s + i.total, 0),
    };
  }, [items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
